import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, CreditCard, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface CardTransaction {
  id: string;
  transaction_number: string | null;
  user_id: string;
  crypto_symbol: string;
  crypto_name: string;
  amount: number;
  total_usd: number;
  price_usd: number;
  card_last_four: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
}

const AdminCardRedemptions = () => {
  const [transactions, setTransactions] = useState<CardTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [profiles, setProfiles] = useState<Record<string, string>>({});

  const fetchData = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("transactions")
      .select("*")
      .eq("card_type", "yqqy_card")
      .order("created_at", { ascending: false })
      .limit(200);

    if (!error && data) {
      setTransactions(data);
      // Fetch profiles for user names
      const userIds = [...new Set(data.map((t) => t.user_id))];
      if (userIds.length > 0) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("user_id, full_name, email")
          .in("user_id", userIds);
        if (profileData) {
          const map: Record<string, string> = {};
          profileData.forEach((p) => {
            map[p.user_id] = p.full_name || p.email;
          });
          setProfiles(map);
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = transactions.filter((t) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      t.transaction_number?.toLowerCase().includes(s) ||
      t.crypto_symbol.toLowerCase().includes(s) ||
      t.card_last_four.includes(s) ||
      profiles[t.user_id]?.toLowerCase().includes(s)
    );
  });

  const totalUsd = filtered.reduce((sum, t) => sum + t.total_usd, 0);

  const statusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">تمت ✅</Badge>;
      case "pending":
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">قيد المعالجة ⏳</Badge>;
      case "rejected":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">مرفوضة ❌</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <CreditCard className="w-5 h-5 text-primary" />
          سجل مشتريات بطاقات yqqy
        </CardTitle>
        <div className="flex items-center gap-2">
          <Input
            placeholder="بحث..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48 h-9 text-sm"
          />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary */}
        <div className="flex gap-4 mb-4 flex-wrap">
          <div className="bg-secondary/50 rounded-lg px-4 py-2 text-sm">
            <span className="text-muted-foreground">إجمالي العمليات:</span>{" "}
            <span className="font-bold text-foreground">{filtered.length}</span>
          </div>
          <div className="bg-secondary/50 rounded-lg px-4 py-2 text-sm">
            <span className="text-muted-foreground">إجمالي المبلغ:</span>{" "}
            <span className="font-bold text-green-400">${totalUsd.toFixed(2)}</span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            لا توجد عمليات شراء ببطاقات yqqy
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">رقم المعاملة</TableHead>
                  <TableHead className="text-right">المستخدم</TableHead>
                  <TableHead className="text-right">العملة</TableHead>
                  <TableHead className="text-right">الكمية</TableHead>
                  <TableHead className="text-right">المبلغ USD</TableHead>
                  <TableHead className="text-right">آخر 4 أرقام</TableHead>
                  <TableHead className="text-right">الحالة</TableHead>
                  <TableHead className="text-right">التاريخ</TableHead>
                  <TableHead className="text-right">ملاحظات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell className="font-mono text-xs">{tx.transaction_number || "-"}</TableCell>
                    <TableCell className="text-sm max-w-[150px] truncate">
                      {profiles[tx.user_id] || tx.user_id.slice(0, 8)}
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold">{tx.crypto_symbol}</span>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{tx.amount}</TableCell>
                    <TableCell className="font-mono text-sm text-green-400">${tx.total_usd.toFixed(2)}</TableCell>
                    <TableCell className="font-mono">****{tx.card_last_four}</TableCell>
                    <TableCell>{statusBadge(tx.status)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(tx.created_at).toLocaleDateString("ar-EG", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate text-muted-foreground">
                      {tx.admin_notes || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCardRedemptions;
